import type { WEBCHAT_POSITIONS, LAUNCHER_BUTTON_SHAPES } from './constants';

// SETO-7699: derived from WEBCHAT_POSITIONS (constants.ts) so the type and the runtime
// validation list can't drift apart. Covers both the launcher button and the chat window itself.
export type WebchatPosition = (typeof WEBCHAT_POSITIONS)[number];

// SETO-7699 (icon-bubble addendum): derived from LAUNCHER_BUTTON_SHAPES (constants.ts), same
// drift-proofing as WebchatPosition above.
export type LauncherButtonShape = (typeof LAUNCHER_BUTTON_SHAPES)[number];

export interface InitOptions {
  tenantId: string;
  agentId: string;
  /** Escape hatch: mount the iframe inside this existing element instead of a default container. */
  staticContainerId?: string;
  /** Embedder-set source-tracking fields (data-affiliate/data-campaign) — see sourceTracking.ts. */
  affiliate?: string;
  campaign?: string;
  /** SETO-7698: opens the widget automatically the first time 'ready' fires -- independent of
   *  floatingButton (SETO-7735), both can be set together. */
  autoOpen?: boolean;
  /** SETO-7699: renders a floating launcher button instead of relying on a tenant-authored
   *  trigger. Renamed from autoOpenButton (SETO-7735) -- see SCRIPT_ATTR's own comment
   *  (constants.ts) for why. */
  floatingButton?: boolean;
  /** SETO-7699: which side (always along the bottom) the widget anchors to -- covers both the
   *  launcher button and the chat window itself (iframe/fallback), so the two always float
   *  together. Already validated by the time it reaches here (readInitOptionsFromScriptTag) -- an
   *  invalid data-webchat-position is dropped, not passed through; the actual 'right' default is
   *  applied at the point the widget is built (bootWidget), not here, so this stays undefined
   *  rather than always-present like affiliate/campaign above. */
  webchatPosition?: WebchatPosition;
  /** SETO-7699 (icon-bubble addendum): 'label' (default) is the "Chat with us" pill; 'icon' is an
   *  icon-only minimized-chat bubble with no visible text (accessible name comes from aria-label
   *  instead). Already validated by the time it reaches here, same undefined-until-defaulted
   *  posture as webchatPosition above. */
  floatingButtonShape?: LauncherButtonShape;
}

export type WebChatEventName = 'ready' | 'open' | 'close' | 'error';

/** Stable install/runtime failure codes — safe for console, DOM attrs, and agent scrapes. */
export type DiagnosticsErrorCode =
  | 'MISSING_ATTRS'
  | 'STATIC_CONTAINER_MISSING'
  | 'INIT_FAILED'
  | 'READY_TIMEOUT'
  | 'IFRAME_LOAD_FAILED'
  | 'CHAT_DISABLED';

export type DiagnosticsState =
  'not-inited' | 'waiting-ready' | 'ready' | 'timed-out' | 'disabled' | 'open';

/** High-level install health — prefer this over scanning raw fields. */
export type DiagnosticsHealth = 'ok' | 'loading' | 'error';

export interface WebChatErrorDetail {
  code: DiagnosticsErrorCode;
  message: string;
}

/** Thrown at init/attach so diagnostics can classify without matching message text. */
export class WebChatInitError extends Error {
  readonly code: DiagnosticsErrorCode;

  constructor(code: DiagnosticsErrorCode, message: string) {
    super(message);
    this.name = 'WebChatInitError';
    this.code = code;
  }
}

/** Listeners may ignore the optional detail (ready/open/close); `error` passes a code. */
export type WebChatEventListener = (detail?: WebChatErrorDetail) => void;

export interface DiagnosticsIssue {
  code: string;
  severity: 'error' | 'warning';
  message: string;
}

/** Raw install/runtime detail — nested under DiagnosticsReport.data. */
export interface DiagnosticsData {
  version: string;
  env: string;
  uiOrigin: string;
  generatedAt: string;
  embedScriptSrc?: string;
  tenantId?: string;
  agentId?: string;
  staticContainerId?: string;
  webchatPosition?: string;
  floatingButtonShape?: string;
  parentOrigin?: string;
  parentPath?: string;
  iframeSrc?: string;
  iframeOrigin?: string;
  state: DiagnosticsState;
  readyAt?: string;
  timedOutAt?: string;
  iframeLoadAt?: string;
  lastError?: WebChatErrorDetail;
  events: Array<{ t: string; type: string; detail?: string }>;
  checks: {
    scriptTagFound: boolean;
    webChatApiPresent: boolean;
    iframeInDom: boolean;
    fallbackInDom: boolean;
    fallbackVisible: boolean;
    triggerCount: number;
  };
}

/**
 * Console diagnostics dump. Top-level is the skim; `data` is the raw detail.
 * `health === 'ok'` means the install looks correct — no separate boolean.
 */
export interface DiagnosticsReport {
  health: DiagnosticsHealth;
  /** One-line status (also printed on the console log line). */
  summary: string;
  issues: DiagnosticsIssue[];
  data: DiagnosticsData;
}

export interface WebChatApi {
  open(): void;
  close(): void;
  addEventListener(event: WebChatEventName, listener: WebChatEventListener): void;
  removeEventListener(event: WebChatEventName, listener: WebChatEventListener): void;
}
